import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBoxOpen,
  FaCheckCircle,
  FaSyncAlt,
  FaWarehouse,
  FaTimes,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import "../styles/Dashboard.css";
import { getData, patchData } from "../services/Request";
import { toast } from "react-toastify";
import { useLoader } from "../context/LoaderContext";

const normaliseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getProductId = (product) =>
  product?.id ??
  product?._id ??
  product?.product_id ??
  product?.productId ??
  product?.hscode ??
  "";

const getProductCount = (product) =>
  Number(
    product?.productCount?.count ??
      product?.count ??
      product?.quantity ??
      product?.stock ??
      0
  );

const sanitizeCount = (value) => {
  const str = String(value ?? "");
  const isNegative = str.trim().startsWith("-");
  const digits = str.replace(/[^0-9]/g, "");
  if (!digits) return isNegative ? "-" : "";
  return `${isNegative ? "-" : ""}${digits}`;
};

export default function Inventory() {
  const { showLoader, hideLoader } = useLoader();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [countDrafts, setCountDrafts] = useState({});
  const [modalProduct, setModalProduct] = useState(null);
  const hasFetchedInitial = useRef(false);

  const syncDraftsFromProducts = (items) => {
    const drafts = {};
    items.forEach((item) => {
      const id = getProductId(item);
      if (id) drafts[id] = sanitizeCount(getProductCount(item));
    });
    setCountDrafts(drafts);
  };

  const fetchProducts = async ({ withLoader = false } = {}) => {
    try {
      if (withLoader) {
        showLoader();
      } else {
        setListLoading(true);
      }
      const response = await getData("api/products?page=1&pageSize=100");
      const items = normaliseList(response);
      setProducts(items);
      syncDraftsFromProducts(items);
    } catch (error) {
      toast.error("Inventory not loaded.");
      console.error("Failed to fetch products for inventory", error);
    } finally {
      if (withLoader) {
        hideLoader();
      } else {
        setListLoading(false);
      }
    }
  };

  useEffect(() => {
    if (hasFetchedInitial.current) return;
    hasFetchedInitial.current = true;
    fetchProducts({ withLoader: true });
  }, []);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return products;
    return products.filter((product) => {
      const name = (product?.name ?? product?.productName ?? "").toLowerCase();
      const desc = (product?.description ?? "").toLowerCase();
      const code = String(product?.hscode ?? "").toLowerCase();
      return name.includes(term) || desc.includes(term) || code.includes(term);
    });
  }, [products, search]);

  const totalSkus = useMemo(() => products.length, [products]);
  const totalUnits = useMemo(
    () => products.reduce((sum, product) => sum + getProductCount(product), 0),
    [products]
  );
  const lowStock = useMemo(
    () => products.filter((product) => getProductCount(product) <= 5).length,
    [products]
  );

  const handleCountInput = (productId, value) => {
    const cleaned = sanitizeCount(value);
    setCountDrafts((prev) => ({
      ...prev,
      [productId]: cleaned,
    }));
  };

  const handleUpdateCount = async (product) => {
    const productId = getProductId(product);
    if (!productId) {
      toast.error("Product id missing.");
      return;
    }
    const draft = sanitizeCount(countDrafts[productId] ?? "0");
    const adjustment = Number(draft);
    const currentCount = getProductCount(product);
    const nextCount = Number.isFinite(adjustment)
      ? currentCount + adjustment
      : currentCount;
    const toastId = toast.loading("Updating count...");
    setUpdatingId(productId);
    try {
      await patchData(`api/products/${productId}/count`, {
        count: Number.isFinite(adjustment) ? adjustment : 0,
      });
      setCountDrafts((prev) => ({
        ...prev,
        [productId]: "0",
      }));
      setProducts((prev) =>
        prev.map((item) =>
          String(getProductId(item)) === String(productId)
            ? {
                ...item,
                count: nextCount,
                productCount: {
                  ...(item.productCount || {}),
                  count: nextCount,
                },
              }
            : item
        )
      );
      await fetchProducts();
      setModalProduct(null);
      toast.update(toastId, {
        render: "Product count updated.",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
    } catch (error) {
      toast.update(toastId, {
        render: error?.message || "Count update failed.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      console.error("Failed to update product count", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const openModal = (product) => {
    const productId = getProductId(product);
    setCountDrafts((prev) => ({
      ...prev,
      [productId]: "0",
    }));
    setModalProduct(product);
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <Navbar />
        <div className="dashboard-page">
          <main className="dashboard">
            <section className="sample-page inventory-page">
              <div className="inventory-hero">
                <div className="inventory-hero__copy">
                  <p className="eyebrow">Inventory</p>
                  <h1>Stock pulse</h1>
                  <p className="lead">
                    Keep an eye on product availability and adjust counts in a
                    couple of clicks.
                  </p>
                  <div className="inventory-hero__actions">
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={() => fetchProducts()}
                      disabled={listLoading}
                    >
                      <FaSyncAlt /> {listLoading ? "Refreshing..." : "Refresh"}
                    </button>
                    <div className="inventory-pills">
                      <span className="pill">
                        <FaCheckCircle /> Synced list (100 max)
                      </span>
                      <span className="pill">
                        <FaBoxOpen /> Live products
                      </span>
                    </div>
                  </div>
                </div>
                <div className="inventory-hero__card">
                  <div className="inventory-hero__icon">
                    <FaWarehouse />
                  </div>
                  <div>
                    <p className="eyebrow">Tracked SKUs</p>
                    <h2>{totalSkus}</h2>
                    <p className="muted-text">Total units: {totalUnits}</p>
                    <p className="muted-text">Low stock items: {lowStock}</p>
                  </div>
                </div>
              </div>

              <div className="inventory-stats">
                <div className="inventory-stat">
                  <p>Products</p>
                  <h3>{totalSkus}</h3>
                  <small>Active SKUs in your catalog</small>
                </div>
                <div className="inventory-stat">
                  <p>Total units</p>
                  <h3>{totalUnits}</h3>
                  <small>Available across all products</small>
                </div>
                <div className="inventory-stat">
                  <p>Low stock</p>
                  <h3>{lowStock}</h3>
                  <small>At or below 5 units</small>
                </div>
              </div>

              <div className="inventory-toolbar">
                <input
                  type="search"
                  placeholder="Search products or HS code"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span className="muted-text">
                  Showing {filteredProducts.length} of {products.length}
                </span>
              </div>

              <div className="inventory-grid">
                {filteredProducts.length === 0 && (
                  <div className="empty-state inventory-empty">
                    {listLoading
                      ? "Loading products..."
                      : "No products found for this filter."}
                  </div>
                )}
                {filteredProducts.map((product, index) => {
                  const productId = getProductId(product);
                  const displayKey =
                    productId || `${product?.hscode ?? "product"}-${index}`;
                  const count = getProductCount(product);
                  const isLow = getProductCount(product) <= 5;
                  return (
                    <button
                      type="button"
                      className="inventory-card"
                      key={displayKey}
                      onClick={() => openModal(product)}
                    >
                      <div className="inventory-card__top">
                        <span className="inventory-chip">
                          {product?.hscode || "No HS Code"}
                        </span>
                        <span
                          className={`inventory-stock ${
                            isLow ? "is-low" : "is-healthy"
                          }`}
                        >
                          {isLow ? "Low stock" : "In stock"}
                        </span>
                      </div>
                      <h3>
                        {product?.name || product?.productName || "Product"}
                      </h3>
                      <p className="muted-text">
                        {product?.description || "No description provided."}
                      </p>
                      <div className="inventory-count-row">
                        <label>Count</label>
                        <strong>{count}</strong>
                      </div>
                      <span className="muted-text">Tap to update count</span>
                    </button>
                  );
                })}
              </div>
              {modalProduct && (
                <div className="modal-backdrop">
                  <div className="modal-card inventory-modal">
                    <button
                      type="button"
                      className="modal-close-btn"
                      onClick={() => setModalProduct(null)}
                      disabled={updatingId === getProductId(modalProduct)}
                      aria-label="Close"
                    >
                      <FaTimes />
                    </button>
                    <h3>Update count</h3>
                    <p className="muted-text">
                      {modalProduct?.name || modalProduct?.productName} (
                      {modalProduct?.hscode || "No HS Code"})
                    </p>
                  <div className="inventory-count-row">
                    <label htmlFor="modal-count">Count</label>
                    <input
                      id="modal-count"
                      type="number"
                      value={
                        countDrafts[getProductId(modalProduct)] ??
                        "0"
                      }
                      onChange={(e) =>
                        handleCountInput(
                          getProductId(modalProduct),
                          e.target.value
                        )
                      }
                    />
                    <small className="muted-text">
                      Add or subtract from current stock (e.g. 5 or -3). Defaults to 0.
                    </small>
                  </div>
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={() => handleUpdateCount(modalProduct)}
                      disabled={updatingId === getProductId(modalProduct)}
                    >
                      {updatingId === getProductId(modalProduct)
                        ? "Saving..."
                        : "Save count"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
